import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type {
  ExtractedDrawing,
  ProjectFilePageRaster,
} from '@shared/models/project-file-drawing.model';
import {
  extractedDrawingKindLabel,
  groupExtractedDrawings,
} from '@shared/helpers/project-file-drawing-groups.helpers';
import { useDrawingCropUrl } from './drawing-crop-preview.hook';

const useStyles = makeStyles()((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingTop: theme.spacing(1),
  },
  buildingSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  buildingTitle: {
    fontWeight: theme.typography.fontWeightBold,
  },
  familyBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  familyTitle: {
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  storeyLabel: {
    marginBottom: theme.spacing(-0.5),
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: theme.spacing(2.5),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  meta: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  previewLink: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: 360,
    minHeight: 220,
    overflow: 'hidden',
    backgroundColor: theme.palette.action.hover,
  },
  preview: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 280,
    objectFit: 'contain',
  },
  previewPlaceholder: {
    width: 360,
    minHeight: 220,
    flexShrink: 0,
    backgroundColor: theme.palette.action.hover,
  },
}));

const DrawingPreview: React.FC<{
  batchId: string;
  fileId: string;
  drawing: ExtractedDrawing;
  pageRaster: ProjectFilePageRaster | null;
}> = ({ batchId, fileId, drawing, pageRaster }) => {
  const { classes } = useStyles();
  const url = useDrawingCropUrl(batchId, fileId, drawing, pageRaster);

  if (!url) {
    return <Box className={classes.previewPlaceholder} />;
  }
  return (
    <a
      className={classes.previewLink}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        className={classes.preview}
        src={url}
        alt={drawing.viewLabel ?? drawing.drawingKind}
      />
    </a>
  );
};

const DrawingCard: React.FC<{
  batchId: string;
  fileId: string;
  drawing: ExtractedDrawing;
  pageRaster: ProjectFilePageRaster | null;
}> = ({ batchId, fileId, drawing, pageRaster }) => {
  const { classes } = useStyles();
  const title =
    drawing.viewLabel?.trim() || extractedDrawingKindLabel(drawing.drawingKind);
  const claimChips = (drawing.paperClaims ?? []).map((claim) => {
    const measured =
      claim.widthMm != null && claim.heightMm != null
        ? `${Math.round(claim.widthMm)} × ${Math.round(claim.heightMm)} mm`
        : null;
    return [claim.source, claim.value, measured]
      .filter((part) => part != null && part !== '')
      .join(' · ');
  });
  const chips = [
    drawing.scale?.trim() ? drawing.scale.trim() : null,
    drawing.paper
      ? [drawing.paper, drawing.paperSource]
          .filter((part) => part != null && part !== '')
          .join(' · ')
      : null,
    ...claimChips,
    drawing.drawingId?.trim() ? drawing.drawingId.trim() : null,
    drawing.renderDpi != null ? `${Math.round(drawing.renderDpi)} DPI` : null,
    drawing.paperWidthMm != null && drawing.paperHeightMm != null
      ? `${Math.round(drawing.paperWidthMm)} × ${Math.round(drawing.paperHeightMm)} mm`
      : null,
    drawing.pixelsPerMeter != null
      ? `${Math.round(drawing.pixelsPerMeter)} px/m`
      : null,
    drawing.pixelWidth != null && drawing.pixelHeight != null
      ? `${drawing.pixelWidth} × ${drawing.pixelHeight} px`
      : null,
    `Page ${drawing.pageIndex + 1}`,
  ].filter((value): value is string => value != null);

  return (
    <Box className={classes.card}>
      <Box className={classes.meta}>
        <Typography variant="subtitle1">{title}</Typography>
        <Box className={classes.chips}>
          {chips.map((label, chipIndex) => (
            <Chip key={`${chipIndex}-${label}`} size="small" label={label} />
          ))}
        </Box>
      </Box>
      <DrawingPreview
        batchId={batchId}
        fileId={fileId}
        drawing={drawing}
        pageRaster={pageRaster}
      />
    </Box>
  );
};

export const ExtractedDrawingsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  batchId: string;
  fileId: string;
  fileName: string;
  drawings: readonly ExtractedDrawing[];
  pageRasters: readonly ProjectFilePageRaster[];
}> = ({ open, onClose, batchId, fileId, fileName, drawings, pageRasters }) => {
  const { classes } = useStyles();
  const groups = useMemo(() => groupExtractedDrawings(drawings), [drawings]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Extracted drawings · {fileName} ({drawings.length})
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No cropped drawings on this file.
          </Typography>
        ) : (
          groups.map((group, index) => {
            const prev = groups[index - 1];
            const showBuilding =
              prev == null || prev.buildingKey !== group.buildingKey;
            const showFamily = showBuilding || prev.familyId !== group.familyId;
            return (
              <Box
                key={`${group.buildingKey}-${group.familyId}-${group.storeyKind}`}
                className={
                  showBuilding ? classes.buildingSection : classes.familyBlock
                }
              >
                {showBuilding && (
                  <Typography variant="h6" className={classes.buildingTitle}>
                    {group.buildingLabel}
                  </Typography>
                )}
                {showFamily && (
                  <Typography variant="caption" className={classes.familyTitle}>
                    {group.familyLabel}
                  </Typography>
                )}
                {group.storeyLabel && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className={classes.storeyLabel}
                  >
                    {group.storeyLabel}
                  </Typography>
                )}
                {group.drawings.map((drawing) => (
                  <DrawingCard
                    key={drawing.id}
                    batchId={batchId}
                    fileId={fileId}
                    drawing={drawing}
                    pageRaster={
                      pageRasters.find(
                        (raster) => raster.pageIndex === drawing.pageIndex,
                      ) ?? null
                    }
                  />
                ))}
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
